import { useState, useMemo, useEffect } from "react";
import { Pode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { updateMangaraSchema, type UpdateMangara, type CorFita, type MangaraWithRelations } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { Plus, Scissors, Pencil, Trash2, ChevronLeft, ChevronRight, Filter, X, Download, Share2, Copy, List, CalendarDays } from "lucide-react";
import { RegistrarMangaraDialog } from "@/components/registrar-mangara-dialog";
import * as XLSX from "xlsx";
import { agruparMangarasPorDia, textoWhatsAppMangara, type DiaMangara } from "@shared/resumo-dia";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { Segmented } from "@/components/layout/segmented";
// Helper function to format date with week number
const formatDateWithWeek = (date: string, weekNum: number | null): string => {
  const formatted = new Date(date + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' });
  if (weekNum === null) {
    return formatted;
  }
  return `${formatted} / S${weekNum}`;
};

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_MANGARAS = {
  setor: "all",
  cor: "all",
  de: "",
  ate: "",
  visao: "lista",
  q: "",
  pagina: 1,
  porPagina: 25,
};
const PERMITIDOS_MANGARAS = {
  visao: ["lista", "por-dia"],
  porPagina: [10, 25, 50, 100],
} as const;

export default function MangarasPage() {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedMangara, setSelectedMangara] = useState<MangaraWithRelations | null>(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Pagination and filter states
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_MANGARAS, PERMITIDOS_MANGARAS);
  const currentPage = filtros.pagina;
  const itemsPerPage = filtros.porPagina;
  const filterSetor = filtros.setor;
  const filterCor = filtros.cor;
  const filterDataInicio = filtros.de;
  const filterDataFim = filtros.ate;

  const visualizacao = filtros.visao as "lista" | "por-dia";
  const busca = filtros.q;
  const [diaCompartilhar, setDiaCompartilhar] = useState<DiaMangara | null>(null);
  const [copiado, setCopiado] = useState(false);

  const { data: mangaras, isLoading: mangarasLoading } = useQuery<MangaraWithRelations[]>({
    queryKey: ["/api/mangaras"],
  });

  // Lista completa para lookups históricos; produtivos para filtros de seleção
  const { setores } = useSetores();
  const { setores: setoresProdutivos } = useSetores({ somenteProdutivos: true });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  const editForm = useForm<UpdateMangara>({
    resolver: zodResolver(updateMangaraSchema),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMangara }) => {
      const response = await apiRequest("PATCH", `/api/mangaras/${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mangaras"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      toast({
        title: "Mangará atualizado!",
        description: "As alterações foram salvas.",
      });
      setEditDialogOpen(false);
      setSelectedMangara(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar mangará",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/mangaras/${id}`);
      return await response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/mangaras"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      if (result?.cicloExcluido && result?.cicloCodigo) {
        toast({
          title: "Mangará excluído!",
          description: `O ciclo ${result.cicloCodigo} ficou sem mangarás e foi removido.`,
        });
      } else {
        toast({
          title: "Mangará excluído!",
          description: "O registro foi removido.",
        });
      }
      setDeleteDialogOpen(false);
      setSelectedMangara(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir mangará",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onEditSubmit = (data: UpdateMangara) => {
    if (!selectedMangara) return;
    updateMutation.mutate({ id: selectedMangara.id, data });
  };

  const handleEdit = (mangara: MangaraWithRelations) => {
    setSelectedMangara(mangara);
    editForm.reset({
      setorId: mangara.setorId,
      corFitaId: mangara.corFitaId,
      dataCorteMangara: mangara.dataCorteMangara,
      quantidadeCachos: mangara.quantidadeCachos,
      observacoes: mangara.observacoes || "",
      usuarioId: mangara.usuarioId,
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (mangara: MangaraWithRelations) => {
    setSelectedMangara(mangara);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedMangara) return;
    deleteMutation.mutate(selectedMangara.id);
  };

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await apiRequest("DELETE", "/api/mangaras/bulk", { ids });
      return await response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/mangaras"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });

      // Reset selection
      setSelectedIds(new Set());

      // Show feedback
      if (result.failed && result.failed.length > 0) {
        toast({
          title: `${result.deletedIds.length} mangarás excluídos`,
          description: `${result.failed.length} falharam: ${result.failed.map((f: any) => f.reason).join(", ")}`,
          variant: "destructive",
        });
      } else {
        const ciclosInfo = result.ciclosExcluidos?.length
          ? ` Ciclos removidos: ${result.ciclosExcluidos.join(", ")}.`
          : "";
        toast({
          title: "Mangarás excluídos!",
          description: `${result.deletedIds.length} registros foram removidos.${ciclosInfo}`,
        });
      }

      // Adjust pagination if current page is now empty
      const newTotalPages = Math.ceil((totalFiltered - result.deletedIds.length) / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        definir({ pagina: newTotalPages });
      }

      setBulkDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir mangarás",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Selection handlers
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (!paginatedMangaras) return;
    const currentPageIds = paginatedMangaras.map(m => m.id);
    const allSelected = currentPageIds.every(id => selectedIds.has(id));
    
    if (allSelected) {
      // Deselect all from current page
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      // Select all from current page
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(Array.from(selectedIds));
  };

  // Busca: filtro NO CLIENTE, dentro do mesmo memo dos outros filtros — assim a
  // paginação e os totais do rodapé seguem o que está na tela. Termo vazio passa
  // tudo, então sem busca nenhum número muda de valor.
  // Filter and paginate mangaras
  const { filteredMangaras, paginatedMangaras, totalPages, totalFiltered, totalMangaras } = useMemo(() => {
    if (!mangaras) return { filteredMangaras: [], paginatedMangaras: [], totalPages: 0, totalFiltered: 0, totalMangaras: 0 };
    
    // Apply filters
    let filtered = [...mangaras];
    if (filterSetor !== "all") {
      filtered = filtered.filter(m => m.setorId === filterSetor);
    }
    if (filterCor !== "all") {
      filtered = filtered.filter(m => m.corFitaId === filterCor);
    }
    if (busca.trim()) {
      filtered = filtered.filter(m => combina(busca, m.setor.nome, m.corFita.nome));
    }
    
    // Filter by date range (data corte)
    if (filterDataInicio) {
      const dataInicio = new Date(filterDataInicio + 'T00:00:00Z');
      filtered = filtered.filter(m => {
        const dataCorteMangara = new Date(m.dataCorteMangara + 'T00:00:00Z');
        return dataCorteMangara >= dataInicio;
      });
    }
    if (filterDataFim) {
      const dataFim = new Date(filterDataFim + 'T00:00:00Z');
      filtered = filtered.filter(m => {
        const dataCorteMangara = new Date(m.dataCorteMangara + 'T00:00:00Z');
        return dataCorteMangara <= dataFim;
      });
    }
    
    // Sort by date (descending) then by setor (ascending alphabetically)
    filtered.sort((a, b) => {
      const dateCompare = new Date(b.dataCorteMangara).getTime() - new Date(a.dataCorteMangara).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.setor.nome.localeCompare(b.setor.nome);
    });
    
    const totalFiltered = filtered.length;
    const totalMangaras = filtered.reduce((sum, m) => sum + m.quantidadeCachos, 0);
    const totalPages = Math.ceil(totalFiltered / itemsPerPage);
    
    // Paginate
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return { filteredMangaras: filtered, paginatedMangaras: paginated, totalPages, totalFiltered, totalMangaras };
  }, [mangaras, filterSetor, filterCor, filterDataInicio, filterDataFim, busca, currentPage, itemsPerPage]);

  const diasMangara = useMemo(() => {
    if (!filteredMangaras || !cores) return [];
    return agruparMangarasPorDia(filteredMangaras as any[], cores as any[]);
  }, [filteredMangaras, cores]);

  // Safeguard: página maior que o total (filtro encolheu a lista, ou link com
  // `?pagina=9` que não existe mais) volta para a última que existe.
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      definir({ pagina: totalPages });
    }
  }, [currentPage, totalPages, definir]);

  /** Mudar filtro sempre volta para a página 1 — o hook não adivinha isso. */
  const handleFilterChange = (type: "setor" | "cor" | "dataInicio" | "dataFim", value: string) => {
    const chave = type === "dataInicio" ? "de" : type === "dataFim" ? "ate" : type;
    definir({ [chave]: value, pagina: 1 });
  };

  const handleClearFilters = () => {
    definir({ setor: "all", cor: "all", de: "", ate: "", pagina: 1 });
  };

  const hasActiveFilters = filterSetor !== "all" || filterCor !== "all" || filterDataInicio !== "" || filterDataFim !== "";

  // Export to Excel
  const handleExportToExcel = () => {
    if (!filteredMangaras || filteredMangaras.length === 0) {
      toast({
        title: "Nenhum dado para exportar",
        description: "Não há mangarás para exportar.",
        variant: "destructive",
      });
      return;
    }

    // Prepare data for export
    const exportData = filteredMangaras.map((mangara) => ({
      "Data Corte": mangara.dataCorteMangara,
      "Cor da Fita": mangara.corFita.nome,
      "Setor": mangara.setor.nome,
      "Variedade": mangara.setor.variedade?.nome ?? "",
      "Quantidade": mangara.quantidadeCachos,
      "Data Prevista": mangara.dataPrevistaColheita,
      "Observações": mangara.observacoes || "",
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mangarás");

    // Generate filename with current date
    const today = new Date().toISOString().split('T')[0];
    const filename = `mangaras_${today}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);

    toast({
      title: "Exportação concluída!",
      description: `${filteredMangaras.length} registros exportados.`,
    });
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageHeader
        titulo="Mangarás"
        descricao="Corte do mangará e marcação para planejamento de colheita"
        acoesSecundarias={
          <Button
            variant="outline"
            onClick={handleExportToExcel}
            disabled={!filteredMangaras || filteredMangaras.length === 0}
            className={ALVO_TOQUE_BOTAO}
            data-testid="button-export-excel"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        }
        acao={
          <Button className={ALVO_TOQUE_BOTAO} onClick={() => setCreateDialogOpen(true)} data-testid="button-new-mangara">
            <Plus className="h-4 w-4 mr-2" />
            Novo mangará
          </Button>
        }
      />

      <div className="space-y-3">
        <PageToolbar
          fim={
            <>
            <PageSearch
              valor={busca}
              onChange={v => definir({ q: v, pagina: 1 })}
              placeholder="Setor ou fita"
              testId="input-busca-mangaras"
            />
            <Segmented
              testId="seg-visualizacao"
              valor={visualizacao}
              onChange={v => definir({ visao: v })}
              opcoes={[
                { id: "lista", label: "Lista", icone: List },
                { id: "por-dia", label: "Por dia", icone: CalendarDays },
              ]}
            />
            </>
          }
        >
          <Select value={filterSetor} onValueChange={(v) => handleFilterChange("setor", v)}>
            <SelectTrigger className="w-[180px] shrink-0" data-testid="filter-setor">
              <SelectValue placeholder="Setor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Setores</SelectItem>
              {setoresProdutivos?.map(setor => (
                <SelectItem key={setor.id} value={setor.id}>{setor.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterCor} onValueChange={(v) => handleFilterChange("cor", v)}>
            <SelectTrigger className="w-[180px] shrink-0" data-testid="filter-cor">
              <SelectValue placeholder="Cor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as Cores</SelectItem>
              {cores?.map(cor => (
                <SelectItem key={cor.id} value={cor.id}>{cor.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="shrink-0"
              data-testid="button-clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Limpar
            </Button>
          )}
        </PageToolbar>

        <PageToolbar
          fim={
            <div className="flex gap-3 text-xs text-muted-foreground whitespace-nowrap">
              <span data-testid="text-total-registros">
                {totalFiltered} {totalFiltered === 1 ? "registro" : "registros"}
              </span>
              <span className="text-muted-foreground">•</span>
              <span data-testid="text-total-mangaras" className="font-medium">
                {totalMangaras} {totalMangaras === 1 ? "mangará" : "mangarás"}
              </span>
            </div>
          }
        >
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Período (Data Corte):</Label>
          <Input
            type="date"
            value={filterDataInicio}
            onChange={(e) => handleFilterChange("dataInicio", e.target.value)}
            className="w-[150px] shrink-0 text-xs"
            data-testid="filter-data-inicio"
          />
          <span className="text-xs text-muted-foreground">até</span>
          <Input
            type="date"
            value={filterDataFim}
            onChange={(e) => handleFilterChange("dataFim", e.target.value)}
            className="w-[150px] shrink-0 text-xs"
            data-testid="filter-data-fim"
          />
        </PageToolbar>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <Card className="border-primary/50">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Pode cap="cacho.excluir"><Checkbox
                  checked={selectedIds.size > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="checkbox-select-all-toolbar"
                /></Pode>
                <span className="text-sm font-medium">
                  {selectedIds.size} {selectedIds.size === 1 ? "selecionado" : "selecionados"}
                </span>
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
                data-testid="button-bulk-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Selecionados
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Por dia view */}
      {visualizacao === 'por-dia' && !mangarasLoading && (
        <div className="space-y-3" data-testid="view-por-dia">
          {diasMangara.length === 0 ? (
            busca.trim()
              ? <NadaEncontrado termo={busca} />
              : <EmptyState icone={CalendarDays} titulo="Nenhum registro encontrado" descricao="Nenhum corte de mangará no recorte escolhido." />
          ) : (
            diasMangara.map(dia => {
              const dataBR = dia.data.split('-').reverse().join('/');
              return (
                <DataCard
                  key={dia.data}
                  titulo={dataBR}
                  meta={
                    <>
                      <span className="font-mono">S{String(dia.semana).padStart(2, '0')}</span>
                      {' · '}
                      <span className="font-mono font-semibold text-foreground">{dia.totalMangaras}</span> mangarás
                      {' · '}
                      {dia.totalSetores} setor{dia.totalSetores !== 1 ? 'es' : ''}
                    </>
                  }
                  acao={
                    <button
                      className="flex items-center gap-1.5 text-xs font-semibold border border-sucesso bg-sucesso-suave text-sucesso rounded-lg px-3 py-1.5 hover:bg-sucesso-suave transition-colors"
                      onClick={() => setDiaCompartilhar(dia)}
                      data-testid={`btn-compartilhar-${dia.data}`}
                    >
                      <Share2 className="h-3.5 w-3.5" />Compartilhar
                    </button>
                  }
                  testId={`dia-card-${dia.data}`}
                >
                  <div className="p-4 space-y-4">
                    {dia.fitas.map(fita => {
                      // Group setores by bloco: /^[A-Z]\d+$/ → first letter; others → 'Outros'
                      const blocos: Record<string, typeof fita.setores> = {};
                      fita.setores.forEach(s => {
                        const key = /^[A-Z]\d+$/.test(s.nome) ? s.nome[0] : 'Outros';
                        if (!blocos[key]) blocos[key] = [];
                        blocos[key].push(s);
                      });
                      const blocoKeys = Object.keys(blocos).sort((a, b) => {
                        if (a === 'Outros') return 1;
                        if (b === 'Outros') return -1;
                        return a.localeCompare(b);
                      });
                      return (
                        <div key={fita.corId}>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="w-2.5 h-2.5 rounded-full border border-black/20 flex-shrink-0" style={{ backgroundColor: fita.hexColor }} />
                            <span className="font-bold text-sm">Fita {fita.corNome}</span>
                            <span className="ml-auto font-mono text-xs text-muted-foreground">{fita.total} mangarás</span>
                          </div>
                          <div className="space-y-2.5">
                            {blocoKeys.map(bloco => (
                              <div key={bloco}>
                                <p className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                                  {bloco === 'Outros' ? 'Outros' : `Bloco ${bloco}`}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {blocos[bloco].map(setor => (
                                    <span key={setor.setorId} className="inline-flex items-center gap-1.5 rounded-lg border bg-muted/40 px-2.5 py-1 text-sm">
                                      <span className="font-semibold">{setor.nome}</span>
                                      {setor.isMinoria && (
                                        <span className="text-xs text-muted-foreground">({setor.variedadeNome.toLowerCase()})</span>
                                      )}
                                      <span className="font-mono font-semibold">{setor.quantidade}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </DataCard>
              );
            })
          )}
        </div>
      )}

      {/* List view */}
      {visualizacao === 'lista' && (mangarasLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : !mangaras || mangaras.length === 0 ? (
        <EmptyState
          icone={Scissors}
          titulo="Nenhum mangará registrado ainda"
          descricao="O corte do mangará é o que abre o ciclo — registre o primeiro para a previsão de colheita começar a existir."
          acao={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />Novo mangará
            </Button>
          }
          testId="empty-mangaras"
        />
      ) : totalFiltered === 0 ? (
        /* Busca sem resultado e filtro sem resultado são coisas diferentes: a
           primeira cita o termo, a segunda oferece limpar os filtros. */
        busca.trim() ? (
          <NadaEncontrado termo={busca} />
        ) : (
          <EmptyState
            icone={Filter}
            titulo="Nenhum mangará com os filtros aplicados"
            descricao="Nenhum registro no recorte de setor, cor e período escolhido."
            acao={
              <Button variant="ghost" onClick={handleClearFilters}>Limpar filtros</Button>
            }
            testId="empty-mangaras-filtro"
          />
        )
      ) : (
        <>
          <DataCard
            rodape={
              <>
                {totalFiltered} {totalFiltered === 1 ? "registro" : "registros"}
                {" · "}
                <b className="text-foreground">{totalMangaras.toLocaleString("pt-BR")}</b> {totalMangaras === 1 ? "mangará" : "mangarás"}
              </>
            }
          >
            <div className="p-4 md:p-0">
              {/* Mobile: Cards View */}
              <div className="space-y-3 md:hidden">
                {paginatedMangaras.map((mangara) => (
                <div
                  key={mangara.id}
                  className="flex flex-col gap-3 p-3 border rounded-lg bg-card hover-elevate"
                  data-testid={`mangara-card-${mangara.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Pode cap="cacho.excluir">
                      <Checkbox
                        checked={selectedIds.has(mangara.id)}
                        onCheckedChange={() => toggleSelect(mangara.id)}
                        data-testid={`checkbox-${mangara.id}`}
                      />
                    </Pode>
                    <span className="text-xs font-mono text-muted-foreground flex-1">
                      {formatDateWithWeek(mangara.dataCorteMangara, mangara.semanaCorteMangara)}
                    </span>
                    <Badge
                      variant="secondary"
                      className="text-xs border"
                      style={{
                        backgroundColor: mangara.corFita.hexColor + "20",
                        borderColor: mangara.corFita.hexColor,
                      }}
                    >
                      {mangara.corFita.nome}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Setor</p>
                      <p className="font-medium">{mangara.setor.nome}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Variedade</p>
                      <p className="font-medium">{mangara.setor.variedade?.nome}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xs">
                      <span className="text-muted-foreground">Qtd:</span>
                      <span className="font-mono font-medium ml-1">{mangara.quantidadeCachos}</span>
                      <span className="text-muted-foreground mx-2">•</span>
                      <span className="text-muted-foreground">Previsão:</span>
                      <span className="font-mono ml-1">{formatDateWithWeek(mangara.dataPrevistaColheita, mangara.semanaPrevistaColheita)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleEdit(mangara)}
                        data-testid={`button-edit-${mangara.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Pode cap="cacho.excluir">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDelete(mangara)}
                        data-testid={`button-delete-${mangara.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      </Pode>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table View */}
            <div className="hidden md:block">
              <DataTable colunas={["3rem", "10rem", "9rem", "8rem", null, "7rem", "10rem", "6rem"]}>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead>
                      <Checkbox
                        checked={paginatedMangaras.every(m => selectedIds.has(m.id))}
                        onCheckedChange={toggleSelectAll}
                        data-testid="checkbox-select-all"
                      />
                    </DataTableHead>
                    <DataTableHead>Data corte</DataTableHead>
                    <DataTableHead>Cor da fita</DataTableHead>
                    <DataTableHead>Setor</DataTableHead>
                    <DataTableHead>Variedade</DataTableHead>
                    <DataTableHead num>Quantidade</DataTableHead>
                    <DataTableHead>Data prevista</DataTableHead>
                    <DataTableHead num>Ações</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {paginatedMangaras.map((mangara) => (
                    <DataTableRow key={mangara.id} data-testid={`mangara-row-${mangara.id}`}>
                      <DataTableCell>
                        <Checkbox
                          checked={selectedIds.has(mangara.id)}
                          onCheckedChange={() => toggleSelect(mangara.id)}
                          data-testid={`checkbox-${mangara.id}`}
                        />
                      </DataTableCell>
                      <DataTableNum className="text-left">
                        {formatDateWithWeek(mangara.dataCorteMangara, mangara.semanaCorteMangara)}
                      </DataTableNum>
                      <DataTableCell>
                        {/* Cor de fita é DADO, vem de `cores.hexColor` — entra por `style`,
                            nunca por classe do Tailwind. */}
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="h-2.5 w-2.5 rounded-full border border-black/20 shrink-0"
                            style={{ backgroundColor: mangara.corFita.hexColor }}
                          />
                          {mangara.corFita.nome}
                        </span>
                      </DataTableCell>
                      {/* O setor é a coluna que responde à pergunta da tela. */}
                      <DataTableCell destaque>{mangara.setor.nome}</DataTableCell>
                      <DataTableCell className="text-muted-foreground truncate">
                        {mangara.setor.variedade?.nome}
                      </DataTableCell>
                      <DataTableNum>{mangara.quantidadeCachos}</DataTableNum>
                      <DataTableNum className="text-left">
                        {formatDateWithWeek(mangara.dataPrevistaColheita, mangara.semanaPrevistaColheita)}
                      </DataTableNum>
                      <DataTableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEdit(mangara)}
                            data-testid={`button-edit-${mangara.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(mangara)}
                            data-testid={`button-delete-${mangara.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </div>
          </div>
        </DataCard>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Itens por página:</span>
                  <Select 
                    value={itemsPerPage.toString()} 
                    onValueChange={(v) => definir({ porPagina: Number(v), pagina: 1 })}
                  >
                    <SelectTrigger className="w-[80px]" data-testid="select-items-per-page">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => definir({ pagina: Math.max(1, currentPage - 1) })}
                      disabled={currentPage === 1}
                      data-testid="button-prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => definir({ pagina: Math.min(totalPages, currentPage + 1) })}
                      disabled={currentPage === totalPages}
                      data-testid="button-next-page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </>
      ))}

      {/* Share dialog */}
      {diaCompartilhar && (
        <Dialog open={!!diaCompartilhar} onOpenChange={(open) => { if (!open) { setDiaCompartilhar(null); setCopiado(false); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Compartilhar no WhatsApp</DialogTitle>
            </DialogHeader>
            <pre className="whitespace-pre-wrap text-sm bg-muted rounded-lg p-4 max-h-72 overflow-y-auto font-sans text-foreground">
              {textoWhatsAppMangara(diaCompartilhar)}
            </pre>
            <DialogFooter className="flex gap-2 sm:justify-start">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  navigator.clipboard.writeText(textoWhatsAppMangara(diaCompartilhar!));
                  setCopiado(true);
                  setTimeout(() => setCopiado(false), 1500);
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                {copiado ? 'Copiado!' : 'Copiar texto'}
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  const texto = textoWhatsAppMangara(diaCompartilhar!);
                  if (typeof navigator.share === 'function') {
                    navigator.share({ text: texto }).catch(() => {
                      window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank');
                    });
                  } else {
                    window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank');
                  }
                }}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Abrir WhatsApp
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Dialog - New Multi-line Form */}
      <RegistrarMangaraDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Editar Mangará</DialogTitle>
            <DialogDescription>
              Atualize as informações do registro.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-setorId">Setor *</Label>
                <Select
                  value={editForm.watch("setorId")}
                  onValueChange={(value) => editForm.setValue("setorId", value)}
                >
                  <SelectTrigger data-testid="edit-select-setor">
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    {setoresProdutivos?.filter(s => s.ativo).map((setor) => (
                      <SelectItem key={setor.id} value={setor.id}>
                        <div>
                          <div className="font-medium">{setor.nome}</div>
                          <div className="text-xs text-muted-foreground">{setor.variedade?.nome}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-corFitaId">Cor da Fita *</Label>
                <Select
                  value={editForm.watch("corFitaId")}
                  onValueChange={(value) => editForm.setValue("corFitaId", value)}
                >
                  <SelectTrigger data-testid="edit-select-cor">
                    <SelectValue placeholder="Selecione a cor" />
                  </SelectTrigger>
                  <SelectContent>
                    {cores?.filter(c => c.ativo).map((cor) => (
                      <SelectItem key={cor.id} value={cor.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: cor.hexColor }}
                          />
                          <span>{cor.nome}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-dataCorteMangara">Data do Corte *</Label>
                <Input
                  id="edit-dataCorteMangara"
                  type="date"
                  className="font-mono"
                  data-testid="edit-input-data-corte"
                  {...editForm.register("dataCorteMangara")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-quantidadeCachos">Quantidade de Cachos *</Label>
                <Input
                  id="edit-quantidadeCachos"
                  type="number"
                  min="1"
                  data-testid="edit-input-quantidade"
                  {...editForm.register("quantidadeCachos", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-observacoes">Observações</Label>
              <Textarea
                id="edit-observacoes"
                placeholder="Observações sobre o corte..."
                data-testid="edit-input-observacoes"
                {...editForm.register("observacoes")}
              />
            </div>

            <DialogFooter className="flex-col-reverse md:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                data-testid="edit-button-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                data-testid="edit-button-submit"
              >
                {updateMutation.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este mangará? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-button-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-button-confirm"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão em massa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir {selectedIds.size} {selectedIds.size === 1 ? "mangará selecionado" : "mangarás selecionados"}?
              Esta ação não pode ser desfeita e os ciclos afetados serão recalculados automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-button-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="bulk-delete-button-confirm"
            >
              {bulkDeleteMutation.isPending ? "Excluindo..." : `Excluir ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
